import { scopedThreadKey } from "@cmmd-forge/client-runtime";
import type {
  GitActionProgressEvent,
  GitRunStackedActionResult,
  GitStackedAction,
  GitStatusResult,
  ScopedThreadRef,
} from "@cmmd-forge/contracts";
import {
  useEffect,
  useEffectEvent,
  useRef,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react";

import {
  buildGitActionProgressStages,
  type DefaultBranchConfirmableAction,
  requiresDefaultBranchConfirmation,
  resolveProgressDescription,
} from "./GitActionsControl.logic";
import { toastManager, type ThreadToastData } from "~/components/ui/toast";
import { buildPrimaryFetchInit, resolvePrimaryEnvironmentHttpUrl } from "~/environments/primary";
import { readLocalApi } from "~/localApi";
import { randomUUID } from "~/lib/utils";

type GitActionToastId = ReturnType<typeof toastManager.add>;

export interface PendingDefaultBranchAction {
  readonly action: DefaultBranchConfirmableAction;
  readonly branchName: string;
  readonly includesCommit: boolean;
  readonly commitMessage?: string;
  readonly onConfirmed?: () => void;
  readonly filePaths?: string[];
}

export interface RunGitActionWithToastInput {
  readonly action: GitStackedAction;
  readonly commitMessage?: string;
  readonly onConfirmed?: () => void;
  readonly skipDefaultBranchPrompt?: boolean;
  readonly statusOverride?: GitStatusResult | null;
  readonly featureBranch?: boolean;
  readonly progressToastId?: GitActionToastId;
  readonly filePaths?: string[];
}

interface ActiveGitActionProgress {
  readonly toastId: GitActionToastId;
  readonly toastData: ThreadToastData | undefined;
  readonly actionId: string;
  title: string;
  phaseStartedAtMs: number | null;
  hookStartedAtMs: number | null;
  hookName: string | null;
  lastOutputLine: string | null;
  currentPhaseLabel: string | null;
}

interface UseGitActionRunnerInput {
  readonly activeThreadRef: ScopedThreadRef | null;
  readonly gitCwd: string | null;
  readonly gitStatus: GitStatusResult | null;
  readonly isDefaultBranch: boolean;
  readonly threadToastData: ThreadToastData | undefined;
  readonly runMutation: {
    readonly mutateAsync: (
      variables: GitRunStackedActionVariables,
    ) => Promise<GitRunStackedActionResult>;
  };
  readonly setPendingDefaultBranchAction: Dispatch<
    SetStateAction<PendingDefaultBranchAction | null>
  >;
  readonly syncThreadBranchAfterGitAction: (result: GitRunStackedActionResult) => void;
  readonly setThreadLinkedPr: (threadKey: string, prUrl: string) => void;
}

interface GitRunStackedActionVariables {
  readonly actionId: string;
  readonly action: GitStackedAction;
  readonly commitMessage?: string;
  readonly featureBranch?: boolean;
  readonly filePaths?: string[];
  readonly onProgress?: (event: GitActionProgressEvent) => void;
}

const ACTIONS_TOUCHING_REMOTE: ReadonlySet<GitStackedAction> = new Set<GitStackedAction>([
  "push",
  "create_pr",
  "commit_push",
  "commit_push_pr",
]);

export function useGitActionRunner(input: UseGitActionRunnerInput) {
  const activeProgressRef = useRef<ActiveGitActionProgress | null>(null);

  const updateActiveProgressToast = useEffectEvent(() => {
    const progress = activeProgressRef.current;
    if (!progress) return;
    toastManager.update(progress.toastId, {
      type: "loading",
      title: progress.title,
      description: resolveProgressDescription(progress),
      timeout: 0,
      data: progress.toastData,
    });
  });

  useEffect(() => {
    const interval = window.setInterval(() => {
      if (activeProgressRef.current) {
        updateActiveProgressToast();
      }
    }, 1000);

    return () => window.clearInterval(interval);
  }, []);

  const runGitActionWithToast = useEffectEvent(async (request: RunGitActionWithToastInput) => {
    const actionStatus = request.statusOverride ?? input.gitStatus;
    const defaultBranchPrompt = resolveDefaultBranchPrompt({
      request,
      actionStatus,
      isDefaultBranch: request.featureBranch ? false : input.isDefaultBranch,
    });
    if (defaultBranchPrompt) {
      input.setPendingDefaultBranchAction(defaultBranchPrompt);
      return;
    }

    request.onConfirmed?.();

    const progress = startGitActionProgress({
      request,
      actionStatus,
      threadToastData: input.threadToastData,
    });
    activeProgressRef.current = progress.activeProgress;

    if (ACTIONS_TOUCHING_REMOTE.has(request.action)) {
      await refreshGitCredentials(input.gitCwd);
    }

    try {
      const result = await input.runMutation.mutateAsync({
        actionId: progress.activeProgress.actionId,
        action: request.action,
        ...(request.commitMessage ? { commitMessage: request.commitMessage } : {}),
        ...(request.featureBranch ? { featureBranch: request.featureBranch } : {}),
        ...(request.filePaths ? { filePaths: request.filePaths } : {}),
        onProgress: (event) =>
          applyProgressEvent({
            event,
            gitCwd: input.gitCwd,
            activeProgressRef,
            updateActiveProgressToast,
          }),
      });

      activeProgressRef.current = null;
      input.syncThreadBranchAfterGitAction(result);
      syncLinkedPullRequest({
        activeThreadRef: input.activeThreadRef,
        result,
        setThreadLinkedPr: input.setThreadLinkedPr,
      });
      publishSuccessToast({
        toastId: progress.toastId,
        result,
        scopedToastData: progress.scopedToastData,
        rerunAction: (action) => {
          void runGitActionWithToast({ action });
        },
      });
    } catch (error) {
      activeProgressRef.current = null;
      toastManager.update(progress.toastId, {
        type: "error",
        title: "Action failed",
        description: error instanceof Error ? error.message : "An error occurred.",
        data: progress.scopedToastData,
      });
    }
  });

  return runGitActionWithToast;
}

function resolveDefaultBranchPrompt(input: {
  readonly request: RunGitActionWithToastInput;
  readonly actionStatus: GitStatusResult | null;
  readonly isDefaultBranch: boolean;
}): PendingDefaultBranchAction | null {
  const actionBranch = input.actionStatus?.branch ?? null;
  if (
    input.request.skipDefaultBranchPrompt === true ||
    !requiresDefaultBranchConfirmation(input.request.action, input.isDefaultBranch) ||
    !actionBranch
  ) {
    return null;
  }
  if (!isDefaultBranchConfirmableAction(input.request.action)) return null;

  return {
    action: input.request.action,
    branchName: actionBranch,
    includesCommit: resolveIncludesCommit({
      action: input.request.action,
      actionStatus: input.actionStatus,
      featureBranch: input.request.featureBranch === true,
    }),
    ...(input.request.commitMessage ? { commitMessage: input.request.commitMessage } : {}),
    ...(input.request.onConfirmed ? { onConfirmed: input.request.onConfirmed } : {}),
    ...(input.request.filePaths ? { filePaths: input.request.filePaths } : {}),
  };
}

function isDefaultBranchConfirmableAction(
  action: GitStackedAction,
): action is DefaultBranchConfirmableAction {
  return (
    action === "push" ||
    action === "create_pr" ||
    action === "commit_push" ||
    action === "commit_push_pr"
  );
}

function resolveIncludesCommit(input: {
  readonly action: GitStackedAction;
  readonly actionStatus: GitStatusResult | null;
  readonly featureBranch: boolean;
}): boolean {
  return (
    (input.action === "commit" ||
      input.action === "commit_push" ||
      input.action === "commit_push_pr") &&
    (input.action === "commit" ||
      !!input.actionStatus?.hasWorkingTreeChanges ||
      input.featureBranch)
  );
}

function startGitActionProgress(input: {
  readonly request: RunGitActionWithToastInput;
  readonly actionStatus: GitStatusResult | null;
  readonly threadToastData: ThreadToastData | undefined;
}): {
  readonly activeProgress: ActiveGitActionProgress;
  readonly toastId: GitActionToastId;
  readonly scopedToastData: ThreadToastData | undefined;
} {
  const progressStages = buildGitActionProgressStages({
    action: input.request.action,
    hasCustomCommitMessage: !!input.request.commitMessage?.trim(),
    hasWorkingTreeChanges: !!input.actionStatus?.hasWorkingTreeChanges,
    featureBranch: input.request.featureBranch === true,
    shouldPushBeforePr:
      input.request.action === "create_pr" &&
      (!input.actionStatus?.hasUpstream || (input.actionStatus?.aheadCount ?? 0) > 0),
  });
  const scopedToastData = input.threadToastData ? { ...input.threadToastData } : undefined;
  const title = progressStages[0] ?? "Running git action...";
  const toastId =
    input.request.progressToastId ??
    toastManager.add({
      type: "loading",
      title,
      description: "Waiting for Git...",
      timeout: 0,
      data: scopedToastData,
    });

  if (input.request.progressToastId) {
    toastManager.update(input.request.progressToastId, {
      type: "loading",
      title,
      description: "Waiting for Git...",
      timeout: 0,
      data: scopedToastData,
    });
  }

  return {
    toastId,
    scopedToastData,
    activeProgress: {
      toastId,
      toastData: scopedToastData,
      actionId: randomUUID(),
      title,
      phaseStartedAtMs: null,
      hookStartedAtMs: null,
      hookName: null,
      lastOutputLine: null,
      currentPhaseLabel: title,
    },
  };
}

function applyProgressEvent(input: {
  readonly event: GitActionProgressEvent;
  readonly gitCwd: string | null;
  readonly activeProgressRef: MutableRefObject<ActiveGitActionProgress | null>;
  readonly updateActiveProgressToast: () => void;
}): void {
  const progress = input.activeProgressRef.current;
  if (!progress) return;
  if (input.gitCwd && input.event.cwd !== input.gitCwd) return;
  if (progress.actionId !== input.event.actionId) return;

  if (!applyMutableProgressEvent(progress, input.event)) return;
  input.updateActiveProgressToast();
}

function applyMutableProgressEvent(
  progress: ActiveGitActionProgress,
  event: GitActionProgressEvent,
): boolean {
  const now = Date.now();
  switch (event.kind) {
    case "action_started":
      resetProgressPhase(progress, now);
      return true;
    case "phase_started":
      progress.title = event.label;
      progress.currentPhaseLabel = event.label;
      resetProgressPhase(progress, now);
      return true;
    case "hook_started":
      progress.title = `Running ${event.hookName}...`;
      progress.hookName = event.hookName;
      progress.hookStartedAtMs = now;
      progress.lastOutputLine = null;
      return true;
    case "hook_output":
      progress.lastOutputLine = event.text;
      return true;
    case "hook_finished":
      progress.title = progress.currentPhaseLabel ?? "Committing...";
      progress.hookName = null;
      progress.hookStartedAtMs = null;
      progress.lastOutputLine = null;
      return true;
    case "action_finished":
    case "action_failed":
      return false;
  }
}

function resetProgressPhase(progress: ActiveGitActionProgress, now: number): void {
  progress.phaseStartedAtMs = now;
  progress.hookStartedAtMs = null;
  progress.hookName = null;
  progress.lastOutputLine = null;
}

function syncLinkedPullRequest(input: {
  readonly activeThreadRef: ScopedThreadRef | null;
  readonly result: GitRunStackedActionResult;
  readonly setThreadLinkedPr: (threadKey: string, prUrl: string) => void;
}): void {
  const prUrl = input.result.pr.url;
  if (
    !input.activeThreadRef ||
    (input.result.pr.status !== "created" && input.result.pr.status !== "opened_existing") ||
    typeof prUrl !== "string" ||
    prUrl.trim().length === 0
  ) {
    return;
  }

  input.setThreadLinkedPr(scopedThreadKey(input.activeThreadRef), prUrl);
}

function publishSuccessToast(input: {
  readonly toastId: GitActionToastId;
  readonly result: GitRunStackedActionResult;
  readonly scopedToastData: ThreadToastData | undefined;
  readonly rerunAction: (action: GitStackedAction) => void;
}): void {
  const actionProps = resolveToastActionProps(input);
  const successToastBase = {
    type: "success",
    title: input.result.toast.title,
    description: input.result.toast.description,
    timeout: 0,
    data: {
      ...input.scopedToastData,
      dismissAfterVisibleMs: 10_000,
    },
  } as const;

  toastManager.update(
    input.toastId,
    actionProps ? { ...successToastBase, actionProps } : successToastBase,
  );
}

function resolveToastActionProps(input: {
  readonly toastId: GitActionToastId;
  readonly result: GitRunStackedActionResult;
  readonly rerunAction: (action: GitStackedAction) => void;
}): { readonly children: string; readonly onClick: () => void } | null {
  const toastCta = input.result.toast.cta;
  if (toastCta.kind === "run_action") {
    return {
      children: toastCta.label,
      onClick: () => {
        toastManager.close(input.toastId);
        input.rerunAction(toastCta.action.kind);
      },
    };
  }
  if (toastCta.kind === "open_pr") {
    return {
      children: toastCta.label,
      onClick: () => {
        const api = readLocalApi();
        if (!api) return;
        toastManager.close(input.toastId);
        void api.shell.openExternal(toastCta.url);
      },
    };
  }
  return null;
}

async function refreshGitCredentials(cwd: string | null): Promise<void> {
  if (!cwd) return;
  try {
    const url = resolvePrimaryEnvironmentHttpUrl("/.well-known/forge/github/refresh-credentials");
    await fetch(
      url,
      buildPrimaryFetchInit({
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ cwd }),
      }),
    );
  } catch {
    // best-effort; push will surface its own auth failure if needed
  }
}
